import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { User, Mail, Phone, AlertCircle } from 'lucide-react';


// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend.skiesseven.com';

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    // Phone validation
    const phoneRegex = /^[0-9]{8,15}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone.replace(/[\s-]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number (8-15 digits)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({}); // Clear any previous errors

    try {
      // Step 1: Validate registration data (doesn't save to DB)
      const validationResponse = await fetch(`${BACKEND_URL}/api/validate-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // ✅ SAFELY parse JSON with error handling
      let validationData;
      try {
        validationData = await validationResponse.json();
      } catch (parseError) {
        console.error('Failed to parse validation response:', parseError);
        throw new Error('Server returned an invalid response. Please try again.');
      }

      if (!validationResponse.ok) {
        const errorMessage = validationData.error || 'Validation failed. Please check your information and try again.';
        
        // Check if it's a duplicate email error
        if (errorMessage.includes('registered') || errorMessage.includes('paid')) {
          throw new Error('This email is already registered and paid. Please use a different email address.');
        }
        
        throw new Error(errorMessage);
      }

      console.log('✓ Validation successful:', validationData);

      // Step 2: Create payment session with customer data
      const paymentResponse = await fetch(`${BACKEND_URL}/api/create-thawani-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          origin_url: window.location.origin,
        }),
      });

      // ✅ SAFELY parse JSON with error handling
      let paymentData;
      try {
        paymentData = await paymentResponse.json();
      } catch (parseError) {
        console.error('Failed to parse payment response:', parseError);
        throw new Error('Server returned an invalid response. Please try again.');
      }

      if (!paymentResponse.ok) {
        const errorMessage = paymentData.error || 'Failed to create payment session. Please try again.';
        
        // Check if it's a duplicate email error
        if (errorMessage.includes('already registered') || errorMessage.includes('already paid')) {
          throw new Error('This email is already registered and paid. Please use a different email address or contact support.');
        }
        
        throw new Error(errorMessage);
      }

      console.log('✓ Payment session created:', paymentData.session_id);

      // Store client_reference_id so we can use it as a backup if needed
      if (paymentData.client_reference_id) {
        try {
          sessionStorage.setItem('payment_client_reference', paymentData.client_reference_id);
          console.log('✓ Stored client_reference_id:', paymentData.client_reference_id);
        } catch (e) {
          console.warn('Unable to store client_reference_id in sessionStorage:', e);
        }
      }
      
      // Step 3: Redirect to payment page
      if (paymentData.payment_url) {
        console.log('✓ Redirecting to payment:', paymentData.payment_url);
        window.location.href = paymentData.payment_url;
      } else {
        throw new Error('No payment URL received. Please try again.');
      }

    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle different types of errors
      let errorMessage = error.message || 'Registration failed. Please try again.';
      
      // Network/connection errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      }
      
      // Set error state for display
      setErrors({
        general: errorMessage
      });
      setIsSubmitting(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      onSubmit={handleSubmit}
      className="glass-effect border border-primary/10 p-8 md:p-12"
    >
      <div className="space-y-6">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm font-bold text-foreground mb-2">
            Full Name *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-3 bg-black/30 border ${
                errors.name ? 'border-destructive' : 'border-primary/20'
              } text-foreground focus:border-primary focus:outline-none transition-colors`}
              placeholder="Enter your full name"
              disabled={isSubmitting}
            />
          </div>
          {errors.name && (
            <p className="text-destructive text-sm mt-1">{errors.name}</p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-bold text-foreground mb-2">
            Email Address *
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-3 bg-black/30 border ${
                errors.email ? 'border-destructive' : 'border-primary/20'
              } text-foreground focus:border-primary focus:outline-none transition-colors`}
              placeholder="your.email@example.com"
              disabled={isSubmitting}
            />
          </div>
          {errors.email && (
            <p className="text-destructive text-sm mt-1">{errors.email}</p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <label htmlFor="phone" className="block text-sm font-bold text-foreground mb-2">
            Phone Number *
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-3 bg-black/30 border ${
                errors.phone ? 'border-destructive' : 'border-primary/20'
              } text-foreground focus:border-primary focus:outline-none transition-colors`}
              placeholder="+968 9693 9694"
              disabled={isSubmitting}
            />
          </div>
          {errors.phone && (
            <p className="text-destructive text-sm mt-1">{errors.phone}</p>
          )}
        </div>

        {/* General Error Message */}
        {errors.general && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-destructive/10 border border-destructive/30 p-4 rounded"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-destructive text-sm font-medium">{errors.general}</p>
                {errors.general.includes('already registered') && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Need help? Contact us at{' '}
                    <a 
                      href="mailto:info@skiesseven.com" 
                      className="text-primary hover:underline"
                    >
                      info@skiesseven.com
                    </a>
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Entry Fee Info */}
        <div className="bg-primary/5 border border-primary/20 p-6 text-center">
          <p className="text-sm text-muted-foreground mb-2">Entry Fee</p>
          <p className="text-3xl font-heading font-bold text-primary">5 OMR</p>
          <p className="text-xs text-muted-foreground mt-2">Per person for all 3 days</p>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full px-8 py-4 bg-primary text-background hover:bg-primary/90 font-bold uppercase tracking-widest transition-all duration-300 neon-glow ${
            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
        </button>

        <p className="text-xs text-center text-muted-foreground">
          By proceeding, you agree to our{' '}
          <Link to="/legal/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
          {' '}and{' '}
          <Link to="/legal/refund" className="text-primary hover:underline">
            Refund Policy
          </Link>
        </p>
      </div>
    </motion.form>
  );
};

export default RegistrationForm;